// Select — styled native <select> with optional leading icon and a
// trailing chevron. Pass `options` or nest <option> children directly.

import { forwardRef, type SelectHTMLAttributes, type ReactNode } from 'react';
import Icon from './Icon';

export type SelectSize = 'sm' | 'md';

export interface SelectOption {
  value: string | number;
  label: string;
}

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  iconStart?: string;
  error?: boolean;
  /** Control height: 'md' (default, 3.5rem) or compact 'sm' (2.5rem). */
  size?: SelectSize;
  /**
   * Size the trigger to its container instead of to its widest option, and
   * ellipsise the selected label. Opt-in, because a native <select> is
   * otherwise floored by its longest <option> — which forces a fixed-width
   * parent (a grid track, a narrow panel) wider than itself and clips. Also
   * exposes the selected label as a hover title so a truncated value stays
   * readable.
   */
  fitContainer?: boolean;
  options?: SelectOption[];
  children?: ReactNode;
}

const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  {
    iconStart,
    error = false,
    size = 'md',
    fitContainer = false,
    options,
    className = '',
    title,
    children,
    ...rest
  },
  ref,
) {
  const classes = [
    'app-select',
    size === 'sm' && 'app-select--sm',
    fitContainer && 'app-select--fit',
    iconStart && 'app-select--with-icon',
    error && 'app-select--error',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const iconSize = size === 'sm' ? 16 : 18;

  // Only the truncating variant needs a title, and only the `options` form can
  // resolve one — the children form has no label to look up. A caller-supplied
  // title always wins.
  const selectedLabel =
    fitContainer && options
      ? options.find((o) => String(o.value) === String(rest.value))?.label
      : undefined;

  return (
    <span className={classes}>
      {iconStart && (
        <Icon name={iconStart} size={iconSize} className='app-select__icon' aria-hidden='true' />
      )}
      <select
        ref={ref}
        className='app-select__control'
        title={title ?? selectedLabel}
        aria-invalid={error || undefined}
        {...rest}
      >
        {options
          ? options.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))
          : children}
      </select>
      <Icon
        name='chevron-down'
        size={iconSize}
        className='app-select__chevron'
        aria-hidden='true'
      />
    </span>
  );
});

export default Select;
